import axios from 'axios';
import { Store } from 'vuex';
import VueRouter from 'vue-router';
import TranslationService from '@/locale/translation.service';

export default class AccountService {
  constructor(
    private store: Store<any>,
    private translationService: TranslationService,
    private router: VueRouter,
  ) {
    this.init();
  }

  public init(): void {
    this.retrieveProfiles();
  }

  public retrieveProfiles(): Promise<boolean> {
    return new Promise(resolve => {
      axios
        .get<any>('management/info')
        .then(res => {
          if (res.data && res.data.activeProfiles) {
            this.store.commit('setRibbonOnProfiles', res.data['display-ribbon-on-profiles']);
            this.store.commit('setActiveProfiles', res.data['activeProfiles']);
          }
          resolve(true);
        })
        .catch(() => resolve(false));
    });
  }

  public retrieveAccount(): Promise<boolean> {
    return new Promise(resolve => {
      axios
        .get<any>('api/account')
        .then(response => {
          this.store.commit('authenticate');
          const account = response.data;
          if (account) {
            this.store.commit('authenticated', account);
            if (this.store.getters.currentLanguage !== account.langKey) {
              this.store.commit('currentLanguage', account.langKey);
            }
            if (sessionStorage.getItem('requested-url')) {
              this.router.replace(sessionStorage.getItem('requested-url'));
              sessionStorage.removeItem('requested-url');
            }
          } else {
            this.store.commit('logout');
            if (this.router.currentRoute.path !== '/') {
              this.router.push('/');
            }
            sessionStorage.removeItem('requested-url');
          }
          this.translationService.refreshTranslation(this.store.getters.currentLanguage);
          resolve(true);
        })
        .catch(() => {
          this.store.commit('logout');
          resolve(false);
        });
    });
  }

  /**
   * Permet de temporiser la promise hasAnyAuthorityAndCheckAuth pour éviter plusieurs appel à retrieveAccount.
   */
  private authorityPromise: Promise<boolean> | null = null;

  public hasAnyAuthorityAndCheckAuth(authorities: any): Promise<boolean> {
    if (typeof authorities === 'string') {
      authorities = [authorities];
    }
    // Si on a déjà les droits en cache et l'utilisateur est encore connecté
    if (this.authenticated && this.userAuthorities) {
      return Promise.resolve(this.checkAuthorities(authorities));
    }

    // Si on est déjà en train de récupérer le compte (évite doublons)
    if (this.authorityPromise) {
      // La promise est déjà en cours
      return this.authorityPromise.then(() => this.checkAuthorities(authorities));
    }
    const token = localStorage.getItem('jhi-authenticationToken') || sessionStorage.getItem('jhi-authenticationToken');
    if (!token) {
      return Promise.resolve(false);
    }

    if (!this.authenticated || !this.userAuthorities) {
      if (!this.store.getters.account && !this.store.getters.logon && token) {
        this.authorityPromise = this.retrieveAccount()
          .then(resp => {
            // Le user est stocké par retrieveAccount
            return true;
          })
          .catch(() => {
            return false;
          })
          .finally(() => {
            this.authorityPromise = null; // reset pour permettre un refresh futur si besoin
          });
      }
      this.authorityPromise = Promise.resolve(false);
    }

    return this.authorityPromise.then(() => this.checkAuthorities(authorities));
  }

  public get authenticated(): boolean {
    return this.store.getters.authenticated;
  }

  public get userAuthorities(): any {
    return this.store.getters.account?.authorities;
  }

  private checkAuthorities(authorities: any): Promise<boolean> {
    if (this.userAuthorities) {
      for (const authority of authorities) {
        if (this.userAuthorities.includes(authority)) {
          return Promise.resolve(true);
        }
      }
    }
    return Promise.resolve(false);
  }
}
